import PQueue from 'p-queue'
import _clone from 'lodash.clonedeep'
import Debug from 'logdown'

import { abortUploadRequest, completeUploadRequest, fetchSignedURLs } from './http'
import { UploadModel } from './models'

import Axios from 'axios'

const ERROR_BASE = 'MultipartUploader:'
const ERROR_CONFIG_VALIDATION = 'Config Error ->'
const DEFAULT_CONFIG_AWS = { forceOverwrite: true, chunkSize: 10 * 1024 * 1024 }

// One dropped connection or S3 5xx must not sink a whole multi-part upload
const PART_MAX_ATTEMPTS = 3
const PART_RETRY_BASE_DELAY = 1000
// A part whose socket dies silently has to fail eventually so it can be retried
const PART_TIMEOUT = 5 * 60 * 1000

const PART_STATUS_IN_PROGRESS = 'In Progress'

const d = new Debug('its:classes:MultipartUploader')

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

// S3 answers a quoted etag; a proxy may hand it over unquoted
function parseEtag (rawEtag) {
  if (!rawEtag) throw new Error('part response carried no ETag header')
  const etag = String(rawEtag).trim()
  return etag.startsWith('"') ? JSON.parse(etag) : etag
}

export default class MultipartUploader {
  constructor ({ awsConfig = {}, concurrencyLimit = 4 }) {
    // Constants
    this.AWS_CONFIG = Object.assign(DEFAULT_CONFIG_AWS, awsConfig)
    this.FILE_CHUNK_SIZE = this.AWS_CONFIG.chunkSize
    this.CONCURRENCY_SIZE = concurrencyLimit

    // Axios Setup
    // eslint-disable-next-line
    this.axios = new Axios.create()
    delete this.axios.defaults.headers.put['Content-Type']

    // PQueue Setup
    this.queue = new PQueue({ concurrency: this.CONCURRENCY_SIZE })

    // Upload Data Setup
    this.uploadData = null

    this.completeResponse = null

    // Every part reports back exactly once, and the upload reports back exactly once
    this.settledParts = 0
    this.isFinalized = false

    // Event Listeners Setup
    this.listeners = {}

    // Validate Config
    MultipartUploader._validateConfig(this)
  }

  // ************************************************************************************
  // Public Methods
  // ************************************************************************************
  upload (file) {
    const config = _clone(this.AWS_CONFIG)

    Promise.resolve()
      .then(() => fetchSignedURLs(config))
      .then((urlData) => {
        if (!urlData || urlData.message !== 'Success') {
          throw new Error(`signed url request failed: ${urlData?.message || 'no response'}`)
        }
        if (!urlData.urls?.length) throw new Error('signed url request returned no part urls')

        this.uploadData = new UploadModel({ ...config, file, signedURLData: urlData })
        this.uploadData.progress.total = file.size

        this._queueParts(file)
      })
      .catch((err) => {
        d.error(`${ERROR_BASE} ${err?.message || err}`)
        this._finalize(ITS__FILE__PROCESSING__STATUS__FAILED)
      })

    return this
  }

  // Events
  onUploadProgress (cb) {
    this.listeners['uploadProgress'] = cb
    return this
  }

  onUploadComplete (cb) {
    this.listeners['uploadComplete'] = cb
    return this
  }

  // ************************************************************************************
  // Private Methods
  // ************************************************************************************
  _queueParts (file) {
    // Mark every part in flight up front, so a part that settles before its siblings
    // have started cannot be read as "all parts are done"
    this.uploadData.parts.forEach((part) => {
      part.progress.status = PART_STATUS_IN_PROGRESS
    })

    this.uploadData.parts.forEach((part, idx) => {
      const start = idx * this.FILE_CHUNK_SIZE
      const end = start + this.FILE_CHUNK_SIZE
      const blob = file.slice(start, end)

      this.queue.add(() => this._uploadPart(part, blob))
    })
  }

  async _uploadPart (part, blob) {
    for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt++) {
      try {
        if (!part.url) throw new Error('no signed url for this part')

        const response = await this.axios.put(part.url, blob, {
          timeout: PART_TIMEOUT,
          onUploadProgress: (progressEvent) => {
            MultipartUploader._updateProgressHandler(this.uploadData, part, progressEvent)
            MultipartUploader._triggerEvent(this.listeners, 'uploadProgress', this.uploadData.progress)
          }
        })

        part.etag = parseEtag(response.headers.etag)
        part.progress.status = ITS__FILE__PROCESSING__STATUS__SUCCESS
        this._partSettled()
        return
      } catch (err) {
        d.error(
          `${ERROR_BASE} part ${part.index + 1} attempt ${attempt}/${PART_MAX_ATTEMPTS} failed: ${err?.message || err}`
        )

        if (attempt === PART_MAX_ATTEMPTS) {
          part.progress.status = ITS__FILE__PROCESSING__STATUS__FAILED
          this._partSettled()
          return
        }

        // The bytes of a failed attempt were counted as sent; take them back off
        part.progress.loaded = 0
        await wait(PART_RETRY_BASE_DELAY * attempt)
      }
    }
  }

  _partSettled () {
    this.settledParts += 1
    if (this.settledParts < this.uploadData.parts.length) return

    const allSucceeded = this.uploadData.parts.every(
      (part) => part.progress.status === ITS__FILE__PROCESSING__STATUS__SUCCESS
    )

    if (allSucceeded) this._completeUpload()
    else this._finalize(ITS__FILE__PROCESSING__STATUS__FAILED)
  }

  async _completeUpload () {
    const { bucket, key, parts, uploadId } = this.uploadData

    try {
      this.completeResponse = await completeUploadRequest({
        bucket,
        key,
        uploadId,
        etags: parts.map((part) => part.etag)
      })
      this._finalize(ITS__FILE__PROCESSING__STATUS__SUCCESS)
    } catch (err) {
      d.error(`${ERROR_BASE} complete request failed: ${err?.message || err}`)
      this._finalize(ITS__FILE__PROCESSING__STATUS__FAILED)
    }
  }

  // The one place an upload reports back. Every failure path lands here too, so the
  // caller is never left waiting on an event that is not coming.
  _finalize (status) {
    if (this.isFinalized) return
    this.isFinalized = true

    if (this.uploadData) this.uploadData.progress.status = status
    if (status === ITS__FILE__PROCESSING__STATUS__FAILED) this._abortUpload()

    MultipartUploader._triggerEvent(this.listeners, 'uploadComplete', status)
  }

  // Leaving the multi-part upload open keeps its parts billed in the bucket for good
  _abortUpload () {
    const { bucket, key, uploadId } = this.uploadData || {}
    if (!bucket || !key || !uploadId) return

    abortUploadRequest({ bucket, key, uploadId }).catch((err) => {
      d.error(`${ERROR_BASE} abort request failed for ${key}: ${err?.message || err}`)
    })
  }

  static _validateConfig (context) {
    if (!context.AWS_CONFIG.bucket) {
      MultipartUploader._throwError(`${ERROR_CONFIG_VALIDATION} "awsConfig.bucket" is missing`)
    }
    if (!context.AWS_CONFIG.key) {
      MultipartUploader._throwError(`${ERROR_CONFIG_VALIDATION} "awsConfig.key" is missing`)
    }
    if (!context.AWS_CONFIG.fileSize) {
      MultipartUploader._throwError(`${ERROR_CONFIG_VALIDATION} "awsConfig.fileSize" is missing`)
    }
    if (!context.AWS_CONFIG.chunkSize) {
      MultipartUploader._throwError(`${ERROR_CONFIG_VALIDATION} "awsConfig.chunkSize" is missing`)
    }
  }

  static _updateProgressHandler (uploadData, part, progressEvent) {
    uploadData.parts[part.index].progress = {
      loaded: progressEvent.loaded,
      total: progressEvent.total,
      status: PART_STATUS_IN_PROGRESS
    }

    const totalProgress = uploadData.progress.total
    const loadedProgress = uploadData.parts
      .map((item) => item.progress.loaded)
      .reduce((acc, curr) => acc + curr, 0)

    uploadData.progress.loaded = loadedProgress
    uploadData.progress.loadPercent = totalProgress ? (loadedProgress / totalProgress) * 100 : 0
  }

  static _triggerEvent (listeners, eventName, data) {
    const cb = listeners[eventName]
    if (cb) cb(data)
  }

  static _throwError (msg) {
    console.error(`${ERROR_BASE} ${msg}`)
  }
}
